<template>
  <form
    novalidate
    class="form search"
    role="search"
    @submit.prevent="submit"
    :class="{'-active': route === 'search'}"
    :aria-hidden="aria ? 'false' : 'true'"
  >
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-12 col-md-9 my-5 no-padding-mobile">
          <input
            ref="input"
            id="search"
            type="search"
            v-model="searchText"
            :placeholder="$t('Search')"
            :aria-label="$t('Search on website')"
            :tabindex="isMobile && !menuOpen ? -1 : 0"
          />
          <button :aria-label="$t('Submit')">
            <SearchIcon />
          </button>
        </div>
        <div class="suggest col-9" v-if="route !== 'search'">
          <div class="container-fluid px-0">
            <p class="suggest-title -upper">
              {{ $t('Suggestions') }}
            </p>
            <ul class="collections row">
              <li v-for="(collection, key) in collections" class="collection col-6 col-md" :key="key">
                <collection-item
                  is-thumbs="true"
                  :link-label="linkLabel"
                  has-collection-link-label="true"
                  h-ci18n="true"
                  :collection="collection"
                ></collection-item>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { mapActions } from 'vuex'
import { getRouteName } from '../utilities'
import SearchIcon from './icons/SearchIcon.vue'
// import CollectionItem from './CollectionItem'

export default {
  name: 'Search',
  props: ['collections', 'aria', 'linkLabel'],
  components: {
    CollectionItem: () => import(/* webpackChunkName: "collection-item-cpt" */ './CollectionItem'),
    SearchIcon
  },
  data() {
    return {
      delay: null,
      toggleActive: false,
      searchText: ''
    }
  },
  methods: {
    ...mapActions('search', ['search']),
    keyup(e) {
      if (e.keyCode === 27) {
        this.$emit('close')
      }
    },
    trapFocus(e) {
      if (!(this.$el === e.target || this.$el.contains(e.target)) && (!e.target.classList || !e.target.classList.contains('search'))) {
        this.$emit('close')
        this.$refs.input.blur()
      } else if (this.menuOpen && this.currentSection !== null) {
        this.$store.dispatch('zenith-ui/rootMenu')
      }
    },
    submit() {
      this.$emit('close')
      this.search({ searchText: this.searchText }).then(() => {
        this.$router.push({ path: this.localizedUrl + '/search', query: { search: this.searchText } })
      })
    }
  },
  computed: {
    localizedUrl() {
      return currentStoreView().url || ''
    },
    route() {
      return getRouteName(this.$route.name)
    },
    searchOpen () {
      return this.$store.state['zenith-ui'].search.open
    },
    menuOpen () {
      return this.$store.state['zenith-ui'].menu.open
    },
    isMobile() {
      return this.$store.state['zenith-ui'].menu.mobile
    },
    currentSection() {
      return this.$store.state['zenith-ui'].menu.item
    }
  },
  mounted() {
    if (window.innerWidth >= 768) {
      this.$refs.input.focus()
    }
    document.addEventListener('keyup', this.keyup)
    // document.addEventListener('click', this.trapFocus, true)
    // document.addEventListener('focus', this.trapFocus, true)
  },
  destroyed() {
    document.removeEventListener('keyup', this.keyup)
    // document.removeEventListener('focus', this.trapFocus, true)
    // document.removeEventListener('click', this.trapFocus, true)
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/core';

.search {
  position: fixed;
  background: var(--_zenith-header-background);
  z-index: 20;
  top: var(--zenith-header-height);
  left: 0;
  width: 100%;

  @include bp(max md) {
    position: relative;
    top: 0;
    padding-top: 0;
    z-index: 50;

    &.-active {
      opacity: 1 !important;
      pointer-events: all !important;
    }

    .no-padding-mobile {
      padding: 0;
    }
  }

  button {
    position: absolute;
    z-index: 40;
    right: 3.1rem;
    top: 50%;
    transform: translateY(-50%);
    margin: 0;
    padding: 0;
    background: none;
    border: none;
    color: var(--zenith-header-search-button);

    /deep/ svg {
      width: 2.4rem;
      height: 2.4rem;
    }

    @include bp(max md) {
      right: 1rem;
    }
  }

  input {
    position: relative;
    z-index: 30;
    border: none;
    outline-offset: 0;
    padding: 1.8rem 5rem 1.8rem 2rem;
    transition: $transition-default-fast;
    text-transform: uppercase;
    color: var(--_zenith-header-text-link);
    font-size: 1.6rem;
    font-weight: 300;
    border-radius: .5rem;
    backdrop-filter: blur(3.5rem);
    background: var(--zenith-white-transparent);

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      font-size: 1.6rem;
      font-weight: 300;
      -webkit-text-fill-color: var(--zenith-theme-primary);
      -webkit-box-shadow: 0 0 0 1000px var(--zenith-header-search-input-block-background) inset;
    }

    &::placeholder {
      opacity: 1; /* Firefox */
      color: var(--zenith-grey);
      font-weight: normal;
    }
    &:-ms-input-placeholder {
      color: var(--zenith-grey);
      font-weight: normal;
    }
    &::-ms-input-placeholder {
      color: var(--zenith-grey);
      font-weight: normal;
    }

    &:focus {
      outline: none;

      @include bp(lg) {
        outline: 1px dashed var(--zenith-header-search-input-focus);
        outline-offset: 4px;
        &:hover {
          outline: 1px dashed transparent;
          outline-offset: 0;
        }
      }

      @include bp(max md) {
        & ~ .suggest {
          display: block;
          opacity: 1;
          pointer-events: all;
          transform: translateY(0);
        }
      }
    }
  }

  .suggest {
    overflow-x: hidden;
    padding-bottom: 10rem;
    max-height: calc(100vh - 21rem);
    overflow-y: auto;

    > .container-fluid {
      padding-top: 3rem;
      border-top: 0.1rem solid var(--_zenith-color-transparent);
    }

    &-title {
      color: var(--_zenith-header-text-link);
      font-size: 1.8rem;
      font-weight: 300;
      margin-bottom: 1.5rem;
    }

    &::-webkit-scrollbar {
      width: 2px;
      height: 2px;
      border-radius: 2px;
      background-color: #DFDFDF;
    }

    &::-webkit-scrollbar-thumb {
      background: #262626;
      border-radius: 2px;
    }

    @include bp(max md) {
      position: absolute;
      display: none;
      background: var(--zenith-header-background);
      margin: 0 -1.5rem;
      max-height: calc(var(--100dvh, 100vh) - 16rem);
      transition: all 0.2s ease-in-out;
      opacity: 0;
      pointer-events: none;
      transform: translateY(-2rem);
    }

    i {
      color: var(--zenith-grey);
      display: block;
      margin-bottom: 2rem;
    }
  }

  .collections {
    transition: all 0.4s ease-in-out;
    padding: 0 .5rem;
    margin-bottom: 0;
    list-style: none;

    li {
      padding: 0 1rem;

      @include bp(max lg) {
        margin-bottom: 2rem;
      }
    }

    .collection {
      /deep/ a {
        &.thumbs {
          justify-content: flex-start;
          height: 100%;
        }

        h3 {
          width: 100%;
        }

        .subtitle {
          display: none;

        }
        @include bp(max md) {
          .font-smaller {
            font-size: 1.2rem;
          }
        }

        &:before {
          display: none;
        }

        .collection-item-link__text {
          margin-right: 0.5rem;
          color: var(--_zenith-header-text-link);
          font-size: 1.2rem;
          font-weight: 300;

          &::before {
            background: var(--_zenith-header-text-link);
          }
        }

        &:hover,
        &:focus {
          .collection-item-link__text::before {
            width: 0;
          }
        }
      }
    }
  }
}
</style>
